import { Autocomplete, Stack, TextField } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import AutoComplete, {
  ISelectType,
} from "../../../components/UI/Inputs/AutoComplete";
import DateInput from "../../../components/UI/Inputs/DateInput";
import useFetch from "../../../hooks/useFetch";
import { ITicketAutomationReceivedRule } from "../../../services/tasySchedMenu.service";
import { ScheduleAppointmentService } from "../../Schedule/appointment.service";
import { ETasyScheduleTypeCode } from "../../Schedule/model";
import { TAppointment } from "../../Schedule/NewSchedule/schema";
import Period from "../../Schedule/NewSchedule/Steps/SearchAvailability/Period";
import ReturnAvailability from "./ReturnAvailability";

interface ISearchAvailabilityProps {
  rule?: ITicketAutomationReceivedRule | null;
  getExamCustomMessage?: (locationId?: string | number | null) => string | null;
  onShowCustomMessage?: (message: string | null) => void;
  onExamProceduresLoaded?: (count: number | null) => void;
}

const AUTOMATION = {
  CONSULTATION: "20",
  EXAM: "21",
  RETURN_CONSULTATION: "25",
  FEATURED_CONSULTATION: "26",
};

const getInsuranceIntegrationId = (insurance: any) =>
  insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

const SearchAvailability: React.FC<ISearchAvailabilityProps> = ({
  rule,
  getExamCustomMessage,
  onShowCustomMessage,
  onExamProceduresLoaded,
}) => {
  const [specialties, setSpecialties] = useState<ISelectType[]>([]);

  const [professionals, setProfessionals] = useState<ISelectType[]>([]);

  const [examGroups, setExamGroups] = useState<ISelectType[]>([]);

  const [procedures, setProcedures] = useState<ISelectType[]>([]);

  const [calendarPreferences, setCalendarPreferences] = useState<ISelectType[]>(
    [],
  );

  const {
    control,
    getValues,
    resetField,
    setValue,
    formState: { errors },
  } = useFormContext<TAppointment>();
  const preferenceError = (errors.search as any)?.calendarPreference?.message as
    | string
    | undefined;
  const speciality = useWatch({ control, name: "search.speciality" });

  const examLocation = useWatch({ control, name: "search.location" as any }) as
    | ISelectType
    | undefined;

  const calendarPreferenceCode = useWatch({
    control,
    name: "search.calendarPreference" as any,
  }) as string | null | undefined;

  const setCalendarPreferenceCode = useCallback(
    (value: string | null) =>
      setValue("search.calendarPreference" as any, value, {
        shouldValidate: true,
      }),
    [setValue],
  );

  const { t } = useTranslation();

  const automationCode = rule?.ticketAutomationCode;
  const isExam = automationCode === AUTOMATION.EXAM;
  const isReturnConsultation =
    automationCode === AUTOMATION.RETURN_CONSULTATION;
  const isFeaturedConsultation =
    automationCode === AUTOMATION.FEATURED_CONSULTATION;

  const showPreferenceSelect = calendarPreferences.length > 1;
  const isPreferencePending = showPreferenceSelect && !calendarPreferenceCode;

  useEffect(() => {
    setValue("search.calendarPreferenceRequired" as any, showPreferenceSelect);
  }, [setValue, showPreferenceSelect]);

  const consultationTasyScheduleTypeCode = useMemo(() => {
    return 3;
  }, []);

  const buildCommonParams = useCallback(() => {
    const { patient, insurance, establishment } = getValues() as any;

    return {
      IndividualId: patient.individualId || patient.id,
      InsuranceIdentifier: Number(getInsuranceIntegrationId(insurance)),
      InsuranceCategoryCode: (insurance as any)?.categoryIntegrationId,
      EstablishmentCode: establishment?.integrationId,
    };
  }, [getValues]);

  const { sendRequest: getSpecialties } = useFetch(
    ScheduleAppointmentService.getSpecialitysTasy2,
  );
  const { sendRequest: getProfessionals, loading: professionalsLoading } =
    useFetch(ScheduleAppointmentService.getProfessionalsTasy2);
  const { sendRequest: getCalendarPreferences } = useFetch(
    ScheduleAppointmentService.getCalendarPreferencesBySpecialtyTasy2,
  );
  const { sendRequest: getExamGroups } = useFetch(
    ScheduleAppointmentService.getExamGroupsTasy2,
  );
  const { sendRequest: getProcedures, loading: proceduresLoading } = useFetch(
    ScheduleAppointmentService.getExamProceduresTasy2,
  );

  const fetchSpecialties = useCallback(async () => {
    if (isExam || isFeaturedConsultation || isReturnConsultation) return;

    const { data, success } = await getSpecialties(buildCommonParams());
    if (data && success) {
      setSpecialties(
        data.map((specialty) => ({
          id: String(specialty.identifier),
          name: specialty.name,
        })),
      );
    }
  }, [
    buildCommonParams,
    getSpecialties,
    isExam,
    isFeaturedConsultation,
    isReturnConsultation,
  ]);

  const fetchCalendarPreferences = useCallback(async () => {
    if (
      !speciality?.id ||
      isExam ||
      isFeaturedConsultation ||
      isReturnConsultation
    )
      return;

    const { data, success } = await getCalendarPreferences({
      ...buildCommonParams(),
      TicketAutomationReceivedRuleId: rule?.id,
      MedicalSpecialtyExternalId: Number(speciality.id),
    });

    if (data && success && data.length > 0) {
      const options = data.map((preference) => ({
        id: String(preference.identifier),
        name: preference.preference,
      }));

      if (options.length === 1) {
        setCalendarPreferenceCode(options[0].id);
      } else {
        setCalendarPreferences(options);
      }
    }
  }, [
    buildCommonParams,
    getCalendarPreferences,
    isExam,
    isFeaturedConsultation,
    rule?.id,
    setCalendarPreferenceCode,
    isReturnConsultation,
    speciality,
  ]);

  const fetchProfessionals = useCallback(async () => {
    if (
      !speciality?.id ||
      isExam ||
      isFeaturedConsultation ||
      isReturnConsultation
    )
      return;

    if (isPreferencePending) return;

    const { data, success } = await getProfessionals({
      ...buildCommonParams(),
      MedicalSpecialtyCode: Number(speciality.id),
      TasyScheduleTypeCode: consultationTasyScheduleTypeCode,
      CalendarPreference: calendarPreferenceCode || undefined,
    });

    if (data && success) {
      setProfessionals(
        data.map((professional) => ({
          id: String(professional.identifier),
          name: professional.individualName || professional.calendarName,
        })),
      );
    }
  }, [
    buildCommonParams,
    calendarPreferenceCode,
    consultationTasyScheduleTypeCode,
    getProfessionals,
    isExam,
    isFeaturedConsultation,
    isPreferencePending,
    isReturnConsultation,
    speciality,
  ]);

  const fetchExamGroups = useCallback(async () => {
    if (!isExam) return;

    const { establishment } = getValues() as any;
    const { data, success } = await getExamGroups({
      EstablishmentCode: establishment?.integrationId,
    });
    if (data && success) {
      setExamGroups(
        data.map((group) => ({
          id: String(group.identifier),
          name: group.name,
        })),
      );
    }
  }, [getExamGroups, getValues, isExam]);

  const fetchProcedures = useCallback(async () => {
    if (!isExam || !examLocation?.id) return;

    setProcedures([]);
    resetField("search.examGroup");
    onExamProceduresLoaded?.(null);
    const { data, success } = await getProcedures({
      ...buildCommonParams(),
      CalendarGroupingIdentifier: Number(examLocation.id),
    });

    if (data && success) {
      onExamProceduresLoaded?.(data.length);
      if (!data.length) {
        const message = getExamCustomMessage?.(examLocation.id);
        if (message) onShowCustomMessage?.(message);
      }

      setProcedures(
        data.map((procedure) => ({
          id: String(
            procedure.procedureInternalCode || procedure.procedureCode,
          ),
          name: procedure.procedureExamName,
          procedureCode: procedure.procedureCode,
          procedureInternalCode: procedure.procedureInternalCode,
          procedureOriginCode: procedure.procedureOriginType,
          calendarIdentifiers: procedure.calendarIdentifiers,
        })),
      );
    }
  }, [
    buildCommonParams,
    examLocation,
    getExamCustomMessage,
    getProcedures,
    isExam,
    onExamProceduresLoaded,
    onShowCustomMessage,
    resetField,
  ]);

  useEffect(() => {
    setValue(
      "search.type",
      isExam ? ETasyScheduleTypeCode.EXAM : ETasyScheduleTypeCode.CONSULTATION,
    );
  }, [isExam, setValue]);

  useEffect(() => {
    fetchSpecialties();
  }, [fetchSpecialties]);

  useEffect(() => {
    fetchCalendarPreferences();
  }, [fetchCalendarPreferences]);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  useEffect(() => {
    fetchExamGroups();
  }, [fetchExamGroups]);

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  return (
    <Stack gap={1} sx={{ pt: 1 }}>
      {isReturnConsultation && (
        <ReturnAvailability
          rule={rule}
        />
      )}

      {!isReturnConsultation && !isExam && !isFeaturedConsultation && (
        <>
          <AutoComplete
            options={specialties}
            getOptionLabel={(option) => option.name}
            onValueSelected={() => {
              setValue("search.professional", null);
              setCalendarPreferenceCode(null);
              setCalendarPreferences([]);
            }}
            size="small"
            label={t("page.schedules.newSchedule.search.speciality")}
            name="search.speciality"
            sx={{ mb: 2 }}
          />
          {showPreferenceSelect && (
            <Autocomplete
              options={calendarPreferences}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              size="small"
              value={
                calendarPreferences.find(
                  (preference) => preference.id === calendarPreferenceCode,
                ) || null
              }
              onChange={(_, value) =>
                setCalendarPreferenceCode(value?.id || null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("page.schedules.newSchedule.search.preference")}
                  error={!!preferenceError}
                  helperText={preferenceError ? t(preferenceError) : undefined}
                />
              )}
              sx={{ mb: 2 }}
            />
          )}
          <AutoComplete
            options={professionals}
            getOptionLabel={(option) => option.name}
            size="small"
            label={t("page.schedules.newSchedule.search.professional")}
            name="search.professional"
            loading={professionalsLoading}
            disable={
              professionalsLoading || !speciality?.id || isPreferencePending
            }
            sx={{ mb: 2 }}
          />
        </>
      )}

      {isExam && (
        <>
          <AutoComplete
            options={examGroups}
            getOptionLabel={(option) => option.name}
            onValueSelected={(value) => {
              resetField("search.examGroup", {
                defaultValue: null,
              });
              setProcedures([]);
              const message = getExamCustomMessage?.(value?.id);
              if (message) onShowCustomMessage?.(message);
            }}
            size="small"
            label={t("page.schedules.newSchedule.search.examType")}
            name="search.location"
            sx={{ mb: 2 }}
          />
          <AutoComplete
            options={procedures}
            getOptionLabel={(option) => option.name}
            size="small"
            label={t("page.schedules.newSchedule.search.procedure")}
            name="search.examGroup"
            loading={proceduresLoading}
            disable={proceduresLoading || !examLocation?.id}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {!isReturnConsultation && (
        <>
          <DateInput
            name="search.date"
            label={t("page.schedules.newSchedule.search.from")}
            sx={{ mb: 1 }}
          />
          <Period />
        </>
      )}
    </Stack>
  );
};

export default memo(SearchAvailability);
